import React, { useMemo } from "react";
import { Notifications } from "@/integration/application/interfaces/notification.interface";
import {
  renderDefaultContent,
  renderScheduleInvite,
  renderStringArrayBody,
  renderTeamInvite,
  renderTeamJoin,
} from "../utils/renderers";
import useUser from "@integration/user/hooks/useUser.ts";
import { isScheduleInvite, isTeamInvite, isTeamJoin } from "../utils/types";

interface NotificationContentProps {
  notification: Notifications;
  compact?: boolean;
}

/**
 * NotificationContent component
 * Displays the content of a notification based on its type
 */
const NotificationContent: React.FC<NotificationContentProps> = React.memo(
  ({ notification, compact = false }) => {
    const { userAuth } = useUser();

    const content = useMemo(() => {
      // For array body
      if (Array.isArray(notification.body)) {
        return renderStringArrayBody(notification, compact);
      }

      if (typeof notification.body === "object") {
        if (isTeamInvite(notification)) {
          return renderTeamInvite(notification, userAuth?.id, compact);
        }

        if (isScheduleInvite(notification)) {
          return renderScheduleInvite(notification, userAuth?.id, compact);
        }

        if (isTeamJoin(notification)) {
          return renderTeamJoin(notification, userAuth?.id, compact);
        }
      }

      return renderDefaultContent(notification, compact);
    }, [notification, userAuth?.id, compact]);

    return (
      <div className={compact ? "mt-1" : "mt-3"}>
        <p
          className={`text-gray-300 ${compact ? "text-sm line-clamp-2" : "leading-relaxed"}`}
        >
          {content}
        </p>
      </div>
    );
  }
);

NotificationContent.displayName = "NotificationContent";

export default NotificationContent;